import React from "react"

type Breakpoint = "base" | "sm" | "md" | "lg" | "xl" | "2xl"

/**
 * Represents a responsive value. It can either be a single value,
 * or a map of breakpoint-specific values.
 */
type ResponsiveProp<T> = T | Partial<Record<Breakpoint, T>>

/**
 * Defines a mapping of CSS variable names to their responsive values.
 * Example:
 * {
 *   "--gap": { base: "8px", md: "16px" },
 *   "--columns": 1
 * }
 */
type CSSVarMap = Record<string, ResponsiveProp<string | number>>

const breakpointMap: Record<Exclude<Breakpoint, "base">, string> = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
}

/**
 * Minifies a CSS string by removing unnecessary whitespace and characters.
 *
 * @param css - The raw CSS string.
 * @returns A minified CSS string.
 */
const minifyCSS = (css: string): string => {
  return css
    .replace(/\s+/g, "") // Remove ALL whitespace
    .replace(/}{/g, "}") // Remove space between rules
    .replace(/;}/g, "}") // Remove trailing semicolons
    .replace(
      /calc\(([^)]+)\)/g,
      (_, expr) =>
        `calc(${
          expr
            .replace(/([+\-*/])/g, " $1 ") // add spaces around operators
            .replace(/\s+/g, " ") // collapse multiple spaces
        })`,
    ) // Preserve spaces within calc expressions
    .trim()
}

/**
 * Generate responsive CSS variables and injects them
 * into the DOM using a <style> tag scoped to a specific selector.
 *
 * This is useful for dynamically setting custom CSS variables based on breakpoints.
 *
 * @param params - An object containing:
 * @param params.cssVars - A map of CSS variables to their responsive values.
 * @param params.selector - A CSS selector (e.g., `.my-component`) to scope the styles.
 * @returns An object containing a `responsiveStyleTag` React element, which should be rendered in your component.
 *
 * @example
 * ```tsx
 * const { responsiveStyleTag } = useResponsiveCSSVariables({
 *   selector: ".my-box",
 *   cssVars: {
 *     "--my-var": { base: "8px", md: "16px", lg: "32px" },
 *     "--my-other-var": "14px",
 *   },
 * });
 *
 * return (
 *   <>
 *     {responsiveStyleTag}
 *     <div className="my-box" style={{ padding: "var(--my-var)", fontSize: "var(--my-other-var)" }}>
 *       Responsive box
 *     </div>
 *   </>
 * );
 * ```
 *
 * You can use a class, id or any other CSS selector to scope the styles.
 *
 * This example would generate the following CSS injected into the document (the CSS is minified):
 *
 * ```css
 * .my-box {
 *   --my-var: 8px;
 *   --my-other-var: 14px;
 * }
 *
 * \@media (min-width: 768px) {
 *   .my-box {
 *     --my-var: 16px;
 *   }
 * }
 *
 * \@media (min-width: 1024px) {
 *   .my-box {
 *     --my-var: 32px;
 *   }
 * }
 * ```
 */
function useResponsiveCSSVariables({
  cssVars,
  selector,
}: {
  cssVars: CSSVarMap
  selector: string
}) {
  const grouped: Record<Breakpoint, string[]> = {
    base: [],
    sm: [],
    md: [],
    lg: [],
    xl: [],
    "2xl": [],
  }

  // Process each CSS variable and its responsive values
  for (const [varName, raw] of Object.entries(cssVars)) {
    // If raw value is an object, use it directly, otherwise wrap in {base: value}
    const values = typeof raw === "object" && raw !== null ? raw : { base: raw }

    // Group CSS declarations by breakpoint
    for (const [bp, val] of Object.entries(values)) {
      if (val == null) continue
      // Add px unit if value is a number, otherwise use as-is
      grouped[bp as Breakpoint].push(`${varName}: ${typeof val === "number" ? `${val}px` : val};`)
    }
  }

  // Generate final CSS string with media queries
  const css = minifyCSS(
    [
      grouped.base.length && `${selector} { ${grouped.base.join(" ")} }`,
      ...Object.entries(breakpointMap)
        .filter(([bp]) => grouped[bp as Breakpoint].length > 0)
        .map(
          ([bp, minWidth]) =>
            `@media (min-width: ${minWidth}) { ${selector} { ${grouped[bp as Breakpoint].join(" ")} } }`,
        ),
    ]
      .filter(Boolean)
      .join("\n"),
  )

  const responsiveStyleTag = css ? React.createElement("style", null, css) : null
  return { responsiveStyleTag }
}
export { minifyCSS, useResponsiveCSSVariables }

export type { CSSVarMap, ResponsiveProp }
